import AdyenCheckout from '@adyen/adyen-web'
import { apiCaller } from '../../../../js/helpers/api-caller'
import { resultCodes } from './adyen-constants'
import { resultCustomCodes } from './custom-constants'
import { paymentEvents } from '../../../../js/document/event-types'

const DEFAULT_OPTIONS = {
  createParams: {},
  initiatePaymentUrl: '',
  submitAdditionalDetailsUrl: '',
  paymentMethodBrand: '',
  extraParams: {},
  bookingNumber: 0,
  parent: null
}

const ERROR_PREFIX = '[AdyenPayment]'

export default class AdyenImplementation {
  constructor (element, options = {}) {
    this.element = element
    this.options = {
      ...DEFAULT_OPTIONS,
      ...options
    }
    this.events = options.events
    this._detailsInFlight = false
    this.telemetry = {
      platform: 'mvc',
      adyenBookingNumber: this.options.bookingNumber,
      adyenPaymentMethodBrand: this.options.paymentMethodBrand,
      adyenPaymentOption: this.options.paymentOption
    }

    this.onSubmit = this._onSubmitHandler.bind(this)
    this.onAdditionalDetails = this._onAdditionalDetailsHandler.bind(this)
    this.onError = this._onErrorHandler.bind(this)
    this.onActionHandled = this._onActionHandledHandler.bind(this)
  }

  async initCheckout () {
    this._setTelemetryContext()
    this.checkout = await AdyenCheckout({
      ...this.options.createParams,
      onAdditionalDetails: this.onAdditionalDetails,
      onSubmit: this.onSubmit,
      onActionHandled: this.onActionHandled,
      onError: this.onError
    })

    const isCardType = this.options.paymentMethodBrand === 'scheme'
    this.checkoutCreated = this.checkout.create(this.options.paymentMethodBrand, {
      onConfigSuccess: this.onConfigSuccess.bind(this),
      ...(isCardType
        ? { ...this._getCardConfiguration(), onFieldValid: this._onFieldValidHandler.bind(this) }
        : {})
    }).mount(this.element)

    if (this.options.paymentMethodBrand !== 'scheme') { this.onConfigSuccess() }
  }

  onConfigSuccess () {
    this.events.emit(paymentEvents.CONFIG_LOADED)
  }

  // Remove the method if paymentType change
  unmountMethod () {
    if (!this.checkoutCreated) { return }
    this.checkoutCreated.remove()
  }

  _getCardConfiguration () {
    const computed = getComputedStyle(this.element)
    const token = name => computed.getPropertyValue(name).trim()

    return {
      disableIOSArrowKeys: false,
      styles: {
        base: {
          fontSize: token('--adyen-field-font-size'),
          padding: token('--adyen-field-padding')
        }
      }
    }
  }

  _onSubmitHandler (state, component) {
    if (!state.isValid) {
      this._logTelemetryInfo('Payment.SubmitBlockedByValidation')
      return
    }

    component.setStatus('loading')
    if (state.isValid) {
      const prepareData = {
        bookingNumber: this.options.bookingNumber,
        paymentMethodBrand: this.options.paymentMethodBrand,
        paymentOption: this.options.paymentOption,
        fullPaymentAmount: this.options.parent.fullPaymentAmount,
        downPaymentAmount: this.options.parent.downPaymentAmount,
        partialPaymentAmount: this.options.parent.partialPaymentAmount,
        groupUserId: this.options.parent.groupUserId,
        contextitemid: this.options.parent.componentId,
        referrerUrl: window.location.href,
        adyenPaymentObject: JSON.stringify(state.data),
        ...this.options.extraParams
      }
      const eventData = {
        paymentMethod: this.options.paymentMethodBrand,
        paymentType: this.options.paymentOption
      }
      this.events.emit(paymentEvents.ON_SUBMIT, eventData)
      this._handleSubmission(prepareData, component, this.options.initiatePaymentUrl, 'InitiatePayment')
    }
  }

  _onAdditionalDetailsHandler (state, component) {
    this._detailsInFlight = true

    const prepareData = {
      adyenAdditionalDetails: JSON.stringify(state.data),
      bookingIdentifier: this.options.bookingNumber
    }
    this._handleSubmission(
      prepareData,
      component,
      this.options.submitAdditionalDetailsUrl,
      'SubmitAdditionalDetails'
    )
  }

  _onErrorHandler (error) {
    const message = (error && error.message) || ''
    const isOutcome = message.indexOf('no payload in response') !== -1 || message === 'Payment Expired'

    // QRLoader/Await report payment outcomes through onError, not technical errors
    if (isOutcome) {
      const detailsRequested = Boolean(this._detailsInFlight)
      this._detailsInFlight = false
      this._logTelemetryInfo('Payment.NotCompleted', { adyenErrorMessage: message, detailsRequested })
      // if details are in flight, _handleResponse emits the real outcome
      if (!detailsRequested) { this.events.emit(paymentEvents.PAYMENT_FAILED) }
      return
    }

    this._logError(error, 'ComponentError', { adyenErrorType: error && error.name })
    this.events.emit(paymentEvents.PAYMENT_ERROR)
  }

  _onFieldValidHandler (data) {
    const isValid = Boolean(data.valid)
    if (isValid) { return }

    this._logTelemetryInfo('Payment.CardFieldInvalid', {
      fieldType: data.fieldType,
      encryptedFieldName: data.encryptedFieldName
    })
  }

  _onActionHandledHandler (data) {
    this._logTelemetryInfo('Payment.ActionHandled', {
      componentType: data && data.componentType,
      actionDescription: data && data.actionDescription
    })
  }

  async _handleSubmission (prepareData, component, url, logSubmissionTag) {
    try {
      const res = await apiCaller(url, { method: 'POST', body: prepareData })

      if (res.success) {
        const response = typeof res.response.adyenPaymentResponse === 'string' ? JSON.parse(res.response.adyenPaymentResponse) : res.response.adyenPaymentResponse

        if (!response) {
          this._failSubmission(component, new Error('Payment response is empty'), 'EmptyPaymentResponse')
          return
        }

        this._setAdyenReference(response)
        this._handleResponse(response, component)
      }
      if (!res.success) {
        const reasonPhrase = res.response?.reasonPhrase
        const parsedResponse = typeof reasonPhrase === 'string' ? JSON.parse(reasonPhrase) : res.response
        if (parsedResponse) { this._setAdyenReference(parsedResponse) }

        if (parsedResponse?.resultCode === resultCustomCodes.BLOCKED) {
          this.events.emit(paymentEvents.PAYMENT_BLOCKED)
          return
        }

        this._failSubmission(component, new Error(res.error), logSubmissionTag + 'Failed')
      }
    } catch (err) {
      this._failSubmission(component, err, logSubmissionTag + 'Exception')
    } finally {
      this._detailsInFlight = false
    }
  }

  _failSubmission (component, error, errorCode) {
    this._logError(error, errorCode)
    this.events.emit(paymentEvents.PAYMENT_ERROR)
  }

  _handleResponse (paymentResponse, component) {
    if (paymentResponse.action) {
      component.handleAction(paymentResponse.action)
    } else {
      switch (paymentResponse.resultCode) {
        case resultCodes.AUTHORISED:
          this.events.emit(paymentEvents.PAYMENT_SUCCESS)
          break
        case resultCodes.PENDING:
        case resultCodes.RECEIVED:
          this.events.emit(paymentEvents.PAYMENT_PENDING)
          break
        case resultCodes.REFUSED:
          this.events.emit(paymentEvents.PAYMENT_FAILED)
          break
        default:
          this.events.emit(paymentEvents.PAYMENT_ERROR, paymentResponse.resultCode)
          break
      }
    }
  }

  _setTelemetryContext () {
    if (!window.newrelic || !window.newrelic.setCustomAttribute) { return }

    Object.keys(this.telemetry).forEach(key => {
      if (this.telemetry[key] !== null && this.telemetry[key] !== undefined) {
        window.newrelic.setCustomAttribute(key, this.telemetry[key])
      }
    })
  }

  _setAdyenReference (response) {
    const additionalData = response.additionalData || {}
    const pspReference = response.pspReference || additionalData.pspReference
    const merchantReference = response.merchantReference || additionalData.merchantReference

    if (pspReference) { this.telemetry.adyenPspReference = pspReference }
    if (merchantReference) { this.telemetry.adyenMerchantReference = merchantReference }

    this._setTelemetryContext()
  }

  _logError (error, errorCode = 'UnknownError', info = {}) {
    if (!window.newrelic || !window.newrelic.noticeError) { return }

    const message = error && error.message ? error.message : String(error)
    const original = error instanceof Error ? error : new Error(message)
    const reported = new Error(`${ERROR_PREFIX}[${errorCode}] ${message}`)
    reported.name = `AdyenPaymentError:${errorCode}`
    reported.stack = original.stack

    window.newrelic.noticeError(reported, { ...this.telemetry, ...info, errorCode })
  }

  _logTelemetryInfo (eventName, eventData) {
    if (!window.newrelic || !window.newrelic.addPageAction) { return }

    window.newrelic.addPageAction(eventName, { level: 'info', ...this.telemetry, ...eventData })
  }
}
